import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { DeliveryNoteOverview } from '../../models/delivery-note.model';
import { DeliveryNoteOverviewService } from './delivery-note-overview.service';
import {
  DeliveryNoteOverviewInitialState,
  DeliveryNoteOverviewLoad,
  DeliveryNoteTrackingLoad,
} from './delivery-note-overview.actions';
import { Tracking } from '../../../../models/monitoring-tracking';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface DeliveryNoteOverviewStateModel {
  overview: DeliveryNoteOverview;
  loaded: boolean;
  tracking: Tracking[];
}

const initialState: DeliveryNoteOverviewStateModel = {
  overview: null,
  loaded: false,
  tracking: [],
};

@State<DeliveryNoteOverviewStateModel>({
  name: 'deliveryNoteOverview',
  defaults: initialState,
})
@Injectable()
export class DeliveryNoteOverviewState {
  constructor(
    private deliveryNoteOverviewService: DeliveryNoteOverviewService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: DeliveryNoteOverviewStateModel): DeliveryNoteOverviewStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: DeliveryNoteOverviewStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getTracking(state: DeliveryNoteOverviewStateModel): Tracking[] {
    return state.tracking;
  }

  @Selector()
  static getOverview(state: DeliveryNoteOverviewStateModel): DeliveryNoteOverview {
    return state.overview;
  }

  @Action(DeliveryNoteOverviewLoad)
  getDeliveryNoteOverview(
    ctx: StateContext<DeliveryNoteOverviewStateModel>,
    { payload }: DeliveryNoteOverviewLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.deliveryNoteOverviewService.getOverviewData(payload).pipe(
      map((overview: DeliveryNoteOverview) => {
        ctx.patchState({ overview, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DeliveryNoteTrackingLoad)
  getDeliveryNoteTracking(
    ctx: StateContext<DeliveryNoteOverviewStateModel>,
    { payload }: DeliveryNoteTrackingLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.deliveryNoteOverviewService.getTracking(payload).pipe(
      map((tracking: Tracking[]) => {
        ctx.patchState({ tracking, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DeliveryNoteOverviewInitialState)
  setInitialState(ctx: StateContext<DeliveryNoteOverviewStateModel>): void {
    ctx.setState(initialState);
  }
}
